import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, FileText, Pencil, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { locationDocumentsService } from '../../services/sponsors';
import SortableRows from './SortableRows';
import UploadFileButton from './UploadFileButton';
import type { LocationDocument } from '../../types/sponsor';

/** The server's limit (LocationDocument::FILE_MAX_BYTES), checked before an upload. */
const DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;

/** "Regolamento 2027.pdf" → "Regolamento 2027", the title a new document starts with. */
const titleFromFilename = (filename: string) => filename.replace(/\.pdf$/i, '').trim() || filename;

const formatSize = (bytes: number | null) => {
  if (bytes == null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The PDFs a trophy or a competition keeps — the regulation, a notice — on its
 * Info page (issue #374). Each has a title, starting as the file's name;
 * they can be renamed, removed and dragged into order, and downloaded from here,
 * which for now is the only place they can be read.
 *
 * On a stage, the circuit's documents are listed under the stage's own,
 * download-only: they are the circuit's to change.
 */
export default function LocationDocumentsCard({ slug, stage }: { slug: string; stage: boolean }) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [documents, setDocuments] = useState<LocationDocument[]>([]);
  const [inherited, setInherited] = useState<LocationDocument[]>([]);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [removing, setRemoving] = useState<LocationDocument | null>(null);

  const fail = useCallback(
    (error: unknown) =>
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      }),
    [t, toast]
  );

  useEffect(() => {
    let cancelled = false;
    locationDocumentsService
      .list(slug)
      .then((rows) => {
        if (cancelled) return;
        setDocuments(rows.data);
        setInherited(rows.inherited);
      })
      .catch((error) => !cancelled && fail(error));
    return () => {
      cancelled = true;
    };
  }, [slug, fail]);

  const handleUploaded = async (signedId: string, file: File) => {
    try {
      const created = await locationDocumentsService.create(slug, { title: titleFromFilename(file.name), signed_id: signedId });
      setDocuments((current) => [...current, created]);
      // Straight into renaming: the file's name is rarely the title it deserves.
      setRenaming({ id: created.id, title: created.title });
    } catch (error) {
      fail(error);
    }
  };

  const handleRename = async () => {
    if (!renaming) return;
    const { id, title } = renaming;
    setRenaming(null);
    if (!title.trim() || documents.find((d) => d.id === id)?.title === title.trim()) return;
    try {
      const updated = await locationDocumentsService.rename(slug, id, title.trim());
      setDocuments((current) => current.map((d) => (d.id === id ? updated : d)));
    } catch (error) {
      fail(error);
    }
  };

  const handleReorder = async (next: LocationDocument[]) => {
    const before = documents;
    setDocuments(next);
    try {
      setDocuments((await locationDocumentsService.reorder(slug, next.map((d) => d.id))).data);
    } catch (error) {
      setDocuments(before);
      fail(error);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    const gone = removing;
    setRemoving(null);
    try {
      await locationDocumentsService.remove(slug, gone.id);
      setDocuments((current) => current.filter((d) => d.id !== gone.id));
    } catch (error) {
      fail(error);
    }
  };

  return (
    <Card data-testid="documents-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{t('gymAdmin.documents.title')}</CardTitle>
          <CardDescription>{t('gymAdmin.documents.help')}</CardDescription>
        </div>
        <UploadFileButton
          accept="application/pdf,.pdf"
          maxBytes={DOCUMENT_MAX_BYTES}
          testId="document-input"
          onUploaded={handleUploaded}
          onError={(reason) =>
            toast({
              title: t(reason === 'too_large' ? 'gymAdmin.documents.tooLarge' : 'gymAdmin.documents.uploadError'),
              variant: 'destructive',
            })
          }
        >
          {t('gymAdmin.documents.add')}
        </UploadFileButton>
      </CardHeader>
      <CardContent className="space-y-4">
        {documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('gymAdmin.documents.empty')}</p>
        ) : (
          <SortableRows
            items={documents}
            onReorder={handleReorder}
            handleLabel={(document) => t('gymAdmin.documents.reorder', { title: document.title })}
            renderRow={(document) =>
              renaming?.id === document.id ? (
                <form
                  className="flex min-w-0 flex-1 items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleRename();
                  }}
                >
                  <Input
                    autoFocus
                    value={renaming.title}
                    maxLength={150}
                    aria-label={t('gymAdmin.documents.titleLabel')}
                    onChange={(e) => setRenaming({ id: document.id, title: e.target.value })}
                    onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)}
                  />
                  <Button type="submit" variant="ghost" size="icon" aria-label={t('gymAdmin.save')}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setRenaming(null)} aria-label={t('common.cancel')}>
                    <X className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <>
                  <DocumentSummary document={document} />
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setRenaming({ id: document.id, title: document.title })}
                      aria-label={t('gymAdmin.documents.rename', { title: document.title })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => setRemoving(document)}
                      aria-label={t('gymAdmin.documents.remove', { title: document.title })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )
            }
          />
        )}

        {stage && inherited.length > 0 && (
          <div className="space-y-2" data-testid="inherited-documents">
            <p className="text-sm font-medium">{t('gymAdmin.documents.inheritedTitle')}</p>
            <ul className="divide-y rounded-md border">
              {inherited.map((document) => (
                <li key={document.id} className="flex items-center gap-3 p-2">
                  <DocumentSummary document={document} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymAdmin.documents.removeTitle', { title: removing?.title ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('gymAdmin.documents.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/** A document's title, file and size, with the link that downloads it. */
function DocumentSummary({ document }: { document: LocationDocument }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{document.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          {document.filename} · {formatSize(document.byte_size)}
        </p>
      </div>
      {document.download_url && (
        <Button variant="ghost" size="icon" asChild>
          <a href={document.download_url} aria-label={t('gymAdmin.documents.download', { title: document.title })}>
            <Download className="h-4 w-4" />
          </a>
        </Button>
      )}
    </div>
  );
}
